import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  Environment,
  ENVIRONMENT_TOKEN,
} from '../../../../../apps/values/environment.token';

export interface FindOptions {
  sort?: Record<string, 1 | -1>;
  limit?: number;
  skip?: number;
  projection?: Record<string, 0 | 1>;
}

export interface InsertResult {
  insertedId: string;
}

export interface UpdateOptions {
  upsert?: boolean;
}

export interface UpdateResult {
  matchedCount: number;
  modifiedCount: number;
  upsertedId?: string;
  upsertedCount?: number;
}

export interface InsertManyOptions {
  ordered?: boolean;
  bypassDocumentValidation?: boolean;
}

export interface UpdateManyOptions {
  upsert?: boolean;
  arrayFilters?: any[];
  collation?: any;
}

export interface DeleteOptions {
  collation?: any;
  hint?: string | Record<string, any>;
}

export interface InsertManyResult {
  insertedCount: number;
  insertedIds: string[];
}

export interface UpdateManyResult {
  matchedCount: number;
  modifiedCount: number;
  upsertedCount?: number;
  upsertedIds?: string[];
}

export interface DeleteResult {
  deletedCount: number;
}

export interface DeleteManyResult {
  deletedCount: number;
}

export class MongoCollection<T = any> {
  constructor(
    private http: HttpClient,
    private baseUrl: string,
    private database: string,
    private collection: string
  ) {}

  findOne(filter: any, options?: { projection?: any }): Observable<T | null> {
    let params = new HttpParams().set('filter', JSON.stringify(filter));
    if (options?.projection) {
      params = params.set('projection', JSON.stringify(options.projection));
    }

    return this.http.get<T | null>(
      `${this.baseUrl}/api/data/${this.collection}/findOne`,
      { params }
    );
  }

  find(filter: any = {}, options?: FindOptions): Observable<T[]> {
    let params = new HttpParams().set('filter', JSON.stringify(filter));
    if (options?.sort)
      params = params.set('sort', JSON.stringify(options.sort));
    if (options?.limit) params = params.set('limit', options.limit.toString());
    if (options?.skip) params = params.set('skip', options.skip.toString());
    if (options?.projection)
      params = params.set('projection', JSON.stringify(options.projection));

    return this.http.get<T[]>(`${this.baseUrl}/api/data/${this.collection}`, {
      params,
    });
  }

  insertOne(document: Partial<T>): Observable<InsertResult> {
    return this.http.post<InsertResult>(
      `${this.baseUrl}/api/data/${this.collection}`,
      document
    );
  }

  insertMany(documents: Partial<T>[], options?: InsertManyOptions): Observable<InsertManyResult> {
    return this.http.post<InsertManyResult>(
      `${this.baseUrl}/api/data/${this.collection}/insertMany`,
      { documents, options }
    );
  }

  updateOne(filter: any, update: any, options?: UpdateOptions): Observable<UpdateResult> {
    return this.http.patch<UpdateResult>(
      `${this.baseUrl}/api/data/${this.collection}`,
      { filter, update, options }
    );
  }

  updateMany(filter: any, update: any, options?: UpdateManyOptions): Observable<UpdateManyResult> {
    return this.http.patch<UpdateManyResult>(
      `${this.baseUrl}/api/data/${this.collection}/updateMany`,
      { filter, update, options }
    );
  }

  count(filter: any = {}): Observable<number> {
    const params = new HttpParams().set('filter', JSON.stringify(filter));
    return this.http
      .get<{ count: number }>(
        `${this.baseUrl}/api/data/${this.collection}/count`,
        { params }
      )
      .pipe(map((response) => response.count));
  }

  aggregate(pipeline: any[]): Observable<T[]> {
    return this.http.post<T[]>(
      `${this.baseUrl}/api/data/${this.collection}/aggregate`,
      { pipeline }
    );
  }

  deleteOne(filter: any, options?: DeleteOptions): Observable<DeleteResult> {
    return this.http.delete<DeleteResult>(
      `${this.baseUrl}/api/data/${this.collection}`,
      { body: { filter, options } }
    );
  }

  deleteMany(filter: any, options?: DeleteOptions): Observable<DeleteManyResult> {
    return this.http.delete<DeleteManyResult>(
      `${this.baseUrl}/api/data/${this.collection}/deleteMany`,
      { body: { filter, options } }
    );
  }
}

export class MongoDatabase {
  constructor(
    private http: HttpClient,
    private baseUrl: string,
    private database: string
  ) {}

  collection<T = any>(collectionName: string): MongoCollection<T> {
    return new MongoCollection<T>(
      this.http,
      this.baseUrl,
      this.database,
      collectionName
    );
  }
}


@Injectable({
  providedIn: 'root',
})
export class MongoDBHttpService {
  private baseUrl: string;

  constructor(
    private http: HttpClient,
    @Inject(ENVIRONMENT_TOKEN) private environment: Environment
  ) {
    this.baseUrl = this.environment.apiUrl;
  }

  db(database: string): MongoDatabase {
    return new MongoDatabase(this.http, this.baseUrl, database);
  }
}
